// src/pages/SettingsPage.jsx
import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Usar variable de entorno para el backend (solo si esta disponible)
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const db = getFirestore();

  const [waApiToken, setWaApiToken] = useState("");
  const [waPhoneId, setWaPhoneId] = useState("");
  const [waTemplateName, setWaTemplateName] = useState("");
  const [waTemplateLang, setWaTemplateLang] = useState("es_MX");
  const [waSendMode, setWaSendMode] = useState("qr");
  const [testPhone, setTestPhone] = useState("");
  const [testImageUrl, setTestImageUrl] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Estados para WhatsApp (Baileys)
  const [waStatus, setWaStatus] = useState("Desconectado");
  const [qr, setQr] = useState(null);

  // Al montar, leemos de Firestore los valores guardados (si existen)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configRef = doc(db, "config", "whatsapp");
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          const data = configSnap.data();
          setWaApiToken(data.wa_api_token || "");
          setWaPhoneId(data.wa_phone_id || "");
          setWaTemplateName(data.wa_template_name || "");
          setWaTemplateLang(data.wa_template_lang || "es_MX");
          setWaSendMode(data.wa_send_mode || "qr");
        }
      } catch (err) {
        console.error("Error al leer configuración de WhatsApp:", err);
        toast.error("No se pudo cargar la configuración.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [db]);

  // Consulta el estado y el QR de Baileys (solo si hay backend)
  useEffect(() => {
    if (!backendUrl) {
      setWaStatus("No disponible (solo frontend)");
      setQr(null);
      return;
    }
    const fetchWaStatus = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/whatsapp/status`);
        const data = await res.json();
        setWaStatus(data.status || "No disponible");
        setQr(data.qr || null);
      } catch {
        setWaStatus("No disponible");
        setQr(null);
      }
    };

    fetchWaStatus();
    const interval = setInterval(fetchWaStatus, 7000); // refresca cada 7 segundos
    return () => clearInterval(interval);
  }, []);

  const handleSaveConfig = async () => {
    const needsTemplate = waSendMode === "template" || waSendMode === "random";
    if (needsTemplate && (!waApiToken.trim() || !waPhoneId.trim() || !waTemplateName.trim())) {
      toast.error("Completa token, phone ID y plantilla de WhatsApp API.");
      return;
    }

    try {
      const configRef = doc(db, "config", "whatsapp");
      await setDoc(
        configRef,
        {
          wa_api_token: waApiToken.trim(),
          wa_phone_id: waPhoneId.trim(),
          wa_template_name: waTemplateName.trim(),
          wa_template_lang: waTemplateLang.trim() || "es_MX",
          wa_send_mode: waSendMode,
        },
        { merge: true }
      );
      toast.success("Configuración guardada correctamente.");
    } catch (err) {
      console.error("Error al guardar configuración:", err);
      toast.error("No se pudo guardar la configuración.");
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow max-w-md mx-auto">
        <p>Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-4">Configuración de WhatsApp</h1>

      <div className="mb-4 p-4 border rounded bg-gray-50">
        <h2 className="font-semibold mb-1">Estado de WhatsApp</h2>
        <div className="text-sm mb-2">{waStatus}</div>
        {waStatus === "QR disponible. Escanéalo." && qr && (
          <div className="flex flex-col items-center">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qr)}&size=220x220`}
              alt="QR de WhatsApp"
              className="my-2 border"
            />
            <span className="text-xs text-gray-500">Escanea este QR en WhatsApp</span>
          </div>
        )}
        {waStatus !== "QR disponible. Escanéalo." && (
          <div className="text-xs text-gray-500">
            {waStatus === "Conectado"
              ? "WhatsApp conectado correctamente."
              : "Esperando conexión..."}
          </div>
        )}
      </div>

      <div className="mb-6">
        <label className="block text-gray-700 mb-1">Metodo de envio:</label>
        <select
          value={waSendMode}
          onChange={(e) => setWaSendMode(e.target.value)}
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring"
        >
          <option value="qr">WhatsApp por QR</option>
          <option value="template">WhatsApp API (plantilla)</option>
          <option value="random">Aleatorio</option>
        </select>
      </div>

      <div className="mb-4 p-4 border rounded bg-gray-50">
        <h2 className="font-semibold mb-3">WhatsApp API (plantillas)</h2>
        <label className="block text-gray-700 mb-1">Token permanente:</label>
        <input
          type="text"
          value={waApiToken}
          onChange={(e) => setWaApiToken(e.target.value)}
          placeholder="Ingresa el token permanente"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring mb-3"
        />
        <label className="block text-gray-700 mb-1">Phone ID:</label>
        <input
          type="text"
          value={waPhoneId}
          onChange={(e) => setWaPhoneId(e.target.value)}
          placeholder="Ingresa el phone ID"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring mb-3"
        />
        <label className="block text-gray-700 mb-1">Nombre de plantilla:</label>
        <input
          type="text"
          value={waTemplateName}
          onChange={(e) => setWaTemplateName(e.target.value)}
          placeholder="ej. recibo_pago_mx"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring mb-3"
        />
        <label className="block text-gray-700 mb-1">Idioma (ej. es_MX):</label>
        <input
          type="text"
          value={waTemplateLang}
          onChange={(e) => setWaTemplateLang(e.target.value)}
          placeholder="es_MX"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring"
        />
      </div>

      <div className="mb-6 p-4 border rounded bg-gray-50">
        <h2 className="font-semibold mb-3">Prueba de plantilla</h2>
        <label className="block text-gray-700 mb-1">Telefono destino:</label>
        <input
          type="text"
          value={testPhone}
          onChange={(e) => setTestPhone(e.target.value)}
          placeholder="521XXXXXXXXXX"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring mb-3"
        />
        <label className="block text-gray-700 mb-1">URL de imagen (opcional):</label>
        <input
          type="text"
          value={testImageUrl}
          onChange={(e) => setTestImageUrl(e.target.value)}
          placeholder="https://..."
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring mb-3"
        />
        <button
          onClick={async () => {
            if (!testPhone.trim()) {
              toast.error("Ingresa un telefono de prueba.");
              return;
            }
            if (!waApiToken.trim() || !waPhoneId.trim() || !waTemplateName.trim()) {
              toast.error("Completa token, phone ID y plantilla.");
              return;
            }
            setIsTesting(true);
            try {
              const bodyParams = [
                "Negocio de prueba",
                "pago",
                new Date().toLocaleDateString("es-MX"),
              ];
              const payload = {
                messaging_product: "whatsapp",
                to: testPhone.trim(),
                type: "template",
                template: {
                  name: waTemplateName.trim(),
                  language: { code: waTemplateLang.trim() || "es_MX" },
                  components: [
                    {
                      type: "body",
                      parameters: bodyParams.map((value) => ({
                        type: "text",
                        text: String(value ?? ""),
                      })),
                    },
                  ],
                },
              };
              if (testImageUrl.trim()) {
                payload.template.components.unshift({
                  type: "header",
                  parameters: [
                    {
                      type: "image",
                      image: { link: testImageUrl.trim() },
                    },
                  ],
                });
              }
              const url = `https://graph.facebook.com/v21.0/${waPhoneId.trim()}/messages`;
              const res = await fetch(url, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${waApiToken.trim()}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
              });
              if (!res.ok) {
                const errPayload = await res.json().catch(() => ({}));
                throw new Error(errPayload?.error?.message || "Error al enviar prueba.");
              }
              toast.success("Prueba enviada.");
            } catch (err) {
              console.error("Error al enviar prueba:", err);
              toast.error("No se pudo enviar la prueba.");
            } finally {
              setIsTesting(false);
            }
          }}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-60"
          disabled={isTesting}
        >
          {isTesting ? "Enviando..." : "Enviar prueba"}
        </button>
      </div>

      <button
        onClick={handleSaveConfig}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        Guardar configuración
      </button>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default SettingsPage;
